import { Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Home } from "./routes/Home";
import { Song } from "./routes/Song";
import { SongEditor } from "./routes/SongEditor";
import { SignIn } from "./routes/SignIn";
import { Setlist } from "./routes/Setlist";
import { Setlists } from "./routes/Setlists";
import { Booklet } from "./routes/Booklet";
import { Artist } from "./routes/Artist";
import { Settings } from "./routes/Settings";
import { Privacy, Terms } from "./routes/Legal";
import { supabase } from "./lib/supabase";
import "./styles/theme.css";
import "./styles/app.css";

/** The account menu. Closes on a click elsewhere, on Escape, or on navigation. */
function AccountMenu({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const place = useLocation();

  useEffect(() => setOpen(false), [place.pathname]);
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const initial = email.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="account" ref={box}>
      <button
        className="btn account-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="avatar" aria-hidden="true">{initial}</span>
        <span className="account-email">{email}</span>
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-head mono small">{email}</div>
          <Link className="menu-item" role="menuitem" to="/setlists">Setlists</Link>
          <Link className="menu-item" role="menuitem" to="/settings">Account</Link>
          <button
            className="menu-item"
            role="menuitem"
            onClick={() => { setOpen(false); void supabase.auth.signOut(); }}
          >Sign out</button>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
    const { data } = supabase.auth.onAuthStateChange((_event, session) =>
      setEmail(session?.user.email ?? null));
    return () => data.subscription.unsubscribe();
  }, []);

  return (
    <div className="shell">
      <nav className="topbar no-print">
        <div className="topbar-inner">
          <Link to="/" className="serif brand">everychord</Link>
          <div className="nav-links">
            <NavLink to="/" end className={({ isActive }) => (isActive ? "nav on" : "nav")}>
              Songs
            </NavLink>
            {email && (
              <NavLink to="/setlists" className={({ isActive }) => (isActive ? "nav on" : "nav")}>
                Setlists
              </NavLink>
            )}
          </div>
          <span className="grow" />
          {email
            ? <AccountMenu email={email} />
            : <Link to="/signin" className="btn btn-primary">Sign in</Link>}
        </div>
      </nav>

      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/song/:slug" element={<Song />} />
          <Route path="/song/:slug/edit" element={<SongEditor />} />
          <Route path="/artist/:name" element={<Artist />} />
          <Route path="/setlists" element={<Setlists />} />
          <Route path="/setlist/:id" element={<Setlist />} />
          <Route path="/setlist/:id/print" element={<Booklet />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
        </Routes>
      </main>

      <footer className="sitefoot no-print">
        <div className="sitefoot-inner">
          <div>
            <div className="serif small-brand">everychord</div>
            <p className="muted small">
              Chords and words for any song, worked out from the recording.
            </p>
          </div>
          <div className="footlinks">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <a href="mailto:hello@everychord.net">Contact</a>
          </div>
        </div>
        <div className="sitefoot-note">
          <p className="muted small">
            Chords are worked out by a machine and corrected by people, so treat them
            as a good first guess. Lyrics belong to their writers and publishers;
            rights holders can write to us and a song will be removed.
          </p>
        </div>
      </footer>
    </div>
  );
}
